import api from './api';
import { createCoalescer } from './requestCoalescer';

// ─── #1872: in-flight coalescing + a concurrency cap on GETs ───────────────
//
// Measured on stage-: one assign-page load = 57 requests, 21 in a single second,
// at a 2-core box, and the SAME 85-byte request is 0.40 s alone vs a 2.87 s
// median at 20-wide. The page is not slow because the work is slow — it asks for
// everything at once and the box thrashes. `getEmployeesBySkill` alone is fetched
// 3x per load with one EXACT duplicate (same call id, same URL, 407 KB).
//
// Fixing it HERE rather than at the call sites is deliberate: the triggers are
// spread across four assign components and several bus listeners, and removing
// the wrong one silently drops a legitimate refresh. Every component already
// goes through this module, so a duplicate becomes impossible by construction.
//
// GET ONLY. A mutation must never be coalesced — two POSTs that look identical
// are two intended writes.
//
// 6 concurrent matches what a browser already enforces per host on HTTP/1.1, so
// it is the ceiling the app was implicitly written against; over HTTP/2 through
// the ALB nothing enforces it, which is how 21-in-a-second happens.
const MAX_CONCURRENT_GETS = 6;
const _gets = createCoalescer({ maxConcurrent: MAX_CONCURRENT_GETS });

// The key must include anything that can change the RESPONSE. `cs3-context-*`
// selects the tenant and the role, so two GETs for one URL under different
// context are different requests and must never share a promise. In-memory only,
// never logged or persisted.
function _getKey(url, headers) {
    const h = headers && typeof headers === 'object' ? headers : {};
    const parts = Object.keys(h).sort().map((k) => k + '=' + h[k]);
    return String(url) + '\u0000' + parts.join('\u0000');
}

// ─── In-memory client/<uuid> response cache (#1442) ────────────────────────
//
// The Vue SPA re-fetches /api/v[23]/client/<uuid> on every route change. On
// large tenants (iatse2: ~3,693 hits/day) this is the single biggest source
// of API volume — and the endpoint hydrates the entire client tree
// (clients + employees + roles + employee_lists + configs), making each
// call a heavy multi-table join.
//
// We cache the resolved axios response keyed by URL with a 5-minute TTL.
// Invalidate on logout / role-switch / client-switch via the helper below.
//
// Scope: only paths that look like `client/<uuid>` (with optional v3 prefix
// or trailing segment lists). Anything else falls through to the live api
// call. Conservative match — if a path includes a sub-resource (e.g.
// `client/<id>/getClientAllInvoices`), no cache.
//
// Trade-off: a cached client tree can be up to TTL_MS stale. For operator
// flows (settings tweaks, config changes) the user expects to see their
// own change immediately; we invalidate on every POST/PUT/DELETE that
// targets `client/<uuid>...` so save-flows always see fresh state on the
// next read.
const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;
const _clientCache = new Map(); // key: url, value: { response, expiresAt }
const CLIENT_PATH_RE = /^v?\d?\/?client\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isCacheableClientPath(url) {
    if (!url) return false;
    // Strip leading slash + optional v2/v3/ prefix to match either
    //   client/<uuid>
    //   v3/client/<uuid>
    //   /api/v2/client/<uuid>
    const stripped = String(url).replace(/^\/?(api\/)?(v\d\/)?/, '');
    return CLIENT_PATH_RE.test(stripped);
}

function _readClientCache(url) {
    const hit = _clientCache.get(url);
    if (!hit) return null;
    if (Date.now() > hit.expiresAt) {
        _clientCache.delete(url);
        return null;
    }
    return hit.response;
}

function _writeClientCache(url, response) {
    _clientCache.set(url, {
        response,
        expiresAt: Date.now() + CLIENT_CACHE_TTL_MS,
    });
}

export function invalidateClientCache() {
    _clientCache.clear();
}

export default {
    get(url, headers = {}) {
        // A cache HIT short-circuits before the queue: it costs nothing and must
        // not wait behind six live requests.
        if (isCacheableClientPath(url)) {
            const cached = _readClientCache(url);
            if (cached) return Promise.resolve(cached);
        }
        return _gets.request(_getKey(url, headers), () => {
            if (isCacheableClientPath(url)) {
                return api.get(url, { headers }).then((response) => {
                    _writeClientCache(url, response);
                    return response;
                });
            }
            return api.get(url, { headers });
        });
    },

    getUrl(fullUrl, headers = {}) {
        return api.get(fullUrl, { headers, baseURL: '' });
    },

    postUrl(fullUrl, data = {}, headers = {}) {
        return api.post(fullUrl, data, { headers, baseURL: '' });
    },

    putUrl(fullUrl, data = {}, headers = {}) {
        return api.put(fullUrl, data, { headers, baseURL: '' });
    },

    delUrl(fullUrl, headers = {}) {
        return api.delete(fullUrl, { headers, baseURL: '' });
    },

    post(url, data = {}, headers = {}) {
        if (typeof url === 'string' && url.indexOf('client/') !== -1) {
            invalidateClientCache();
        }
        return api.post(url, data, { headers });
    },

    put(url, data = {}, headers = {}) {
        if (typeof url === 'string' && url.indexOf('client/') !== -1) {
            invalidateClientCache();
        }
        return api.put(url, data, { headers });
    },

    del(url, headers = {}) {
        if (typeof url === 'string' && url.indexOf('client/') !== -1) {
            invalidateClientCache();
        }
        return api.delete(url, { headers });
    },

    getAlienAPI(url) {
        return api.get(url, { baseURL: '' });
    },

    getWithBaseUrl(fullUrl, headers = {}) {
        return api.get(fullUrl, { headers, baseURL: '' });
    },

    deviceDetector() {
        let userAgent = navigator.userAgent.toLowerCase();
        const check = function () {
            return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent) ? "tablet" : /(mobi|ipod|phone|blackberry|opera mini|fennec|minimo|symbian|psp|nintendo ds|archos|skyfire|puffin|blazer|bolt|gobrowser|iris|maemo|semc|teashark|uzard)/.test(userAgent) ? "phone" : "desktop"
        };
        return {
            device: check(),
            isMobile: "desktop" != check() ? !0 : !1
        }
    }

};
